import { useAreas, useProjects, useServices } from "@hq/client-react";
import type { Project } from "@hq/contracts";
import { Select, toast } from "@z/ui";
import { Box, Plus } from "lucide-react";
import { useRef, useState } from "react";
import { ProjectAreaDialog } from "./ProjectAreaDialog";

export function ProjectAreaSelect({ project }: { project: Project }) {
  const services = useServices();
  const { data: projects = [] } = useProjects();
  const { data: savedAreas = [] } = useAreas();
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const areas = [
    ...new Set([
      ...savedAreas.map((area) => area.name),
      ...projects.flatMap((entry) => (entry.area ? [entry.area] : [])),
      ...(project.area ? [project.area] : []),
    ]),
  ].sort((a, b) => a.localeCompare(b));
  const items = [
    { value: "none", label: "No area" },
    ...areas.map((area) => ({ value: `area:${area}`, label: area })),
    { value: "new", label: "New area…" },
  ];
  const changeArea = async (value: string | null) => {
    if (value === null || inFlight.current) return;
    if (value === "new") {
      setCreating(true);
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      await services.projects.setArea(project.id, value === "none" ? null : value.slice(5));
    } catch {
      toast({ title: "Could not change area", variant: "danger" });
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };
  return (
    <>
      <Select.Root
        value={project.area ? `area:${project.area}` : "none"}
        items={items}
        disabled={saving}
        onValueChange={(value) => void changeArea(value)}
      >
        <Select.Trigger aria-label="Change project area" className="w-auto max-w-48">
          <span className="flex min-w-0 items-center gap-2">
            <Box aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
            <span className="truncate">{project.area ?? "No area"}</span>
          </span>
        </Select.Trigger>
        <Select.Content>
          {items.map(({ value, label }) => (
            <Select.Item key={value} value={value}>
              <span className="flex items-center gap-2">
                {value === "new" && <Plus aria-hidden="true" className="size-4 text-fg-muted" />}
                {label}
              </span>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
      {creating && <ProjectAreaDialog project={project} onClose={() => setCreating(false)} />}
    </>
  );
}
